import type {
  BackupPolicySecurityConfig,
  Condition,
  Configuration,
} from '@repo/api-gw-sdk';
import {
  CreateInfectedExcludePathApi,
  DeleteInfectedExcludePathApi,
  GetSecurityPlanApi,
  IsSecurityPlanEnabledApi,
  ListInfectedExcludePathsApi,
  SaveSecurityPlanApi,
  SecurityScansApi,
  UpdateInfectedExcludePathApi,
  UpdateSecurityScanPoliciesConfigApi,
} from '@repo/api-gw-sdk';

import type { HttpClient } from './httpClient';

const baseUrl = '/security';

export const security = (
  httpClient: HttpClient,
  config: Configuration,
  currentProjectId: string
) => {
  const securityScansApi = new SecurityScansApi(config);
  const getSecurityPlanApi = new GetSecurityPlanApi(config);
  const isSecurityPlanEnabledApi = new IsSecurityPlanEnabledApi(config);
  const saveSecurityPlanApi = new SaveSecurityPlanApi(config);
  const updateSecurityScanPoliciesConfigApi =
    new UpdateSecurityScanPoliciesConfigApi(config);
  const listInfectedExcludePathsApi = new ListInfectedExcludePathsApi(config);
  const createInfectedExcludePathApi = new CreateInfectedExcludePathApi(config);
  const deleteInfectedExcludePathApi = new DeleteInfectedExcludePathApi(config);
  const updateInfectedExcludePathApi = new UpdateInfectedExcludePathApi(config);

  return {
    useScansSummary: (resourceId: string) =>
      httpClient.execute(
        securityScansApi.getSecurityScansSummary.bind(securityScansApi),
        baseUrl,
        'getScansSummary',
        resourceId,
        currentProjectId
      ),
    useCountInfectedResources: () =>
      httpClient.execute(
        securityScansApi.countInfectedResources.bind(securityScansApi),
        baseUrl,
        'count',
        currentProjectId
      ),
    excludeResource: (resourceId: string) =>
      securityScansApi.updateResourceSecurityScansExclusion(
        currentProjectId,
        resourceId,
        {
          excluded: true,
        }
      ),
    cancelResourceExclusion: (resourceId: string) =>
      securityScansApi.updateResourceSecurityScansExclusion(
        currentProjectId,
        resourceId,
        {
          excluded: false,
        }
      ),
    updatePolicies: (policies: BackupPolicySecurityConfig[]) =>
      updateSecurityScanPoliciesConfigApi.updateSecurityScanPoliciesConfig(
        currentProjectId,
        { policiesConfig: policies }
      ),
    useIsSecurityPlanEnabled: () =>
      httpClient.execute(
        isSecurityPlanEnabledApi.isSecurityPlanEnabled.bind(
          isSecurityPlanEnabledApi
        ),
        baseUrl,
        'isSecurityPlanEnabled',
        currentProjectId
      ),
    useSecurityPlan: () =>
      httpClient.execute(
        getSecurityPlanApi.getSecurityPlan.bind(getSecurityPlanApi),
        baseUrl,
        'getSecurityPlan',
        currentProjectId
      ),
    saveSecurityPlan: (
      condition: Condition | undefined,
      include: string[] = [],
      exclude: string[] = []
    ) =>
      saveSecurityPlanApi.saveSecurityPlan(currentProjectId, {
        condition,
        include,
        exclude,
      }),
    pathExclusion: {
      usePathExclusion: () => {
        return httpClient.execute(
          listInfectedExcludePathsApi.listInfectedExcludePaths.bind(
            listInfectedExcludePathsApi
          ),
          baseUrl,
          'getPathsExclusion',
          currentProjectId
        );
      },
      create: (path: string, providerResourceId?: string) => {
        return createInfectedExcludePathApi.createInfectedExcludePath(
          currentProjectId,
          { path, providerResourceId }
        );
      },
      update: (id: string, path: string, providerResourceId?: string) => {
        return updateInfectedExcludePathApi.updateInfectedExcludePath(
          currentProjectId,
          { id, path, providerResourceId }
        );
      },
      delete: (id: string) => {
        return deleteInfectedExcludePathApi.deleteInfectedExcludePath(
          currentProjectId,
          id
        );
      },
    },
  };
};
